import { Resizable, ResizeCallback } from "re-resizable";
import { useState } from "react";

import * as S from "./ResizableTableView.styled";

interface ListWrapperProps {
  storageKey: string;
  defaultHeight?: number;
  list: React.ReactNode;
  map?: React.ReactNode;

  noResize?: boolean;
}

const getHeightFromStorage = (storageKey: string, target: "map" | "list") => {
  const height = localStorage.getItem(`${storageKey}-${target}`);
  return height ? Number(height) : undefined;
};

const setHeightInStorage = (storageKey: string, target: "map" | "list", height: number) => {
  localStorage.setItem(`${storageKey}-${target}`, String(height));
};

export const ResizableTableView = ({
  storageKey,
  defaultHeight = 50,
  list,
  map,
  noResize = false
}: ListWrapperProps) => {
  const [listHeightInPercents, setListHeightInPercents] = useState(
    getHeightFromStorage(storageKey, "list") ?? defaultHeight
  );

  const [mapHeightInPercents, setMapHeightInPercents] = useState(
    getHeightFromStorage(storageKey, "map") ?? defaultHeight
  );

  const calculateContentProportions: ResizeCallback = (_, __, element, delta) => {
    const parentHeight = element.parentElement?.clientHeight;

    if (!parentHeight) {
      return;
    }

    const nextHeightInPixels = (listHeightInPercents / 100) * parentHeight + delta.height;
    const nextListHeightInPercents = (nextHeightInPixels / parentHeight) * 100;

    const nextMapHeightInPercents = 100 - nextListHeightInPercents;

    setListHeightInPercents(nextListHeightInPercents);
    setMapHeightInPercents(nextMapHeightInPercents);

    setHeightInStorage(storageKey, "list", nextListHeightInPercents);
    setHeightInStorage(storageKey, "map", nextMapHeightInPercents);
  };

  return (
    <>
      <Resizable
        as={S.ResizableRoot}
        size={{
          height: `${listHeightInPercents}%`,
          width: "initial"
        }}
        enable={
          noResize
            ? false
            : {
                bottom: true
              }
        }
        handleComponent={{
          bottom: <S.HandleIcon data-testid="resize-handle" />
        }}
        onResizeStop={calculateContentProportions}
      >
        <S.ResizableListWrapper>{list}</S.ResizableListWrapper>
      </Resizable>

      {map && (
        <S.MapWrapper
          sx={{
            height: `${mapHeightInPercents}%`
          }}
        >
          {map}
        </S.MapWrapper>
      )}
    </>
  );
};
